/**
 * UnmergeOptionsModal
 * 
 * Multi-step modal for unmerging channels from a property.
 * Two flows:
 * 1. Unmerge one channel - select which channels to disconnect
 * 2. Unmerge all channels - disconnect all channels at once
 */

import React, { memo, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  Box,
  Typography,
  Button,
  CircularProgress,
  Checkbox,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { T_KEYS } from '../../constants';
import { IMsoolUnit, ILinkedUnit, ChannelType } from '../../types';
import { useUnmapUnits } from '../../services/mapping.service';

// Import unified channel logo components
import { ChannelLogo as UnifiedChannelLogo, ChannelLogoType } from '../ChannelLogos';

// Styled Components
const StyledDialog = styled(Dialog)({
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.48)',
    backdropFilter: 'blur(2.6px)',
  },
  '& .MuiDialog-paper': {
    borderRadius: '24px',
    maxWidth: '526px',
    width: '100%',
    padding: '0',
    margin: '16px',
    boxShadow: '2px 4px 20px 0px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
  },
});

const ContentContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'start',
  padding: '32px',
  gap: '32px',
  backgroundColor: '#ffffff',
});

const Title = styled(Typography)({
  fontFamily: '"IBM Plex Sans Arabic", "Tajawal", sans-serif',
  fontSize: '24px',
  fontWeight: 700,
  lineHeight: '32px',
  color: '#262626',
  textAlign: 'start',
  width: '100%',
});

const Description = styled(Typography)({
  fontFamily: '"IBM Plex Sans Arabic", "Tajawal", sans-serif',
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '24px',
  color: '#666666',
  textAlign: 'start',
  width: '100%',
});

const OptionsContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  width: '100%',
});

const OptionCard = styled(Box)<{ $selected?: boolean }>(({ $selected }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px',
  backgroundColor: $selected ? 'rgba(91, 47, 249, 0.1)' : '#F8F8F8',
  border: $selected ? '1px solid #5B2FF9' : 'none',
  borderRadius: '16px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  overflow: 'hidden',
}));

const OptionContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  textAlign: 'right',
});

const OptionTitle = styled(Typography)({
  fontFamily: '"IBM Plex Sans Arabic", "Tajawal", sans-serif',
  fontSize: '16px',
  fontWeight: 700,
  lineHeight: '24px',
  color: '#262626',
});

const OptionDescription = styled(Typography)({
  fontFamily: '"IBM Plex Sans Arabic", "Tajawal", sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20px',
  color: '#666666',
});

const RadioCircle = styled(Box)<{ $selected?: boolean }>(({ $selected }) => ({
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  backgroundColor: $selected ? '#5B3FFF' : 'transparent',
  display: $selected ? 'flex' : 'none',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
}));

const CheckIcon = styled('svg')({
  width: '13px',
  height: '13px',
  color: '#ffffff',
});

const ChannelsList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  width: '100%',
});

const ChannelItem = styled(Box)<{ $selected?: boolean }>(({ $selected }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '12px 16px',
  backgroundColor: $selected ? 'rgba(91, 63, 255, 0.05)' : '#F9FAFB',
  border: $selected ? '2px solid #5B3FFF' : '1px solid #E5E7EB',
  borderRadius: '12px',
  cursor: 'pointer',
  gap: '12px',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: '#5B3FFF',
  },
}));

// Note: Local ChannelLogo styled component removed - using unified ChannelLogo component instead

const ChannelName = styled(Typography)({
  fontFamily: '"IBM Plex Sans Arabic", "Tajawal", sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '20px',
  color: '#262626',
  flex: 1,
});

// Horizontal channel cards for select-channels step (matching Figma)
const ChannelCardsContainer = styled(Box)({
  display: 'flex',
  gap: '16px',
  width: '100%',
});

const ChannelCard = styled(Box)<{ $selected?: boolean }>(({ $selected }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  padding: '20px 16px',
  border: $selected ? '2px solid #5B3FFF' : '1px solid #E5E7EB',
  borderRadius: '20px',
  cursor: 'pointer',
  gap: '16px',
  transition: 'all 0.2s ease',
  backgroundColor: $selected ? 'rgba(91, 63, 255, 0.05)' : 'transparent',
  '&:hover': {
    borderColor: '#5B3FFF',
  },
}));

const ChannelCardLogo = styled(Box)<{ $bgColor?: string }>(({ $bgColor }) => ({
  width: '48px',
  height: '48px',
  borderRadius: '14.4px',
  backgroundColor: $bgColor || 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
}));

const ChannelCardLogoImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'contain',
});

const ChannelCardName = styled(Typography)({
  fontFamily: '"IBM Plex Sans Arabic", "Tajawal", sans-serif',
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: '24px',
  color: '#262626',
  textAlign: 'center',
});

const SectionTitle = styled(Typography)({
  fontFamily: '"IBM Plex Sans Arabic", "Tajawal", sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '20px',
  color: '#262626',
  textAlign: 'right',
  width: '100%',
});

const DisabledButton = styled(Button)({
  flex: 1,
  height: '44px',
  borderRadius: '8px',
  backgroundColor: '#DDDDDD',
  fontFamily: '"IBM Plex Sans Arabic", "Tajawal", sans-serif',
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: '24px',
  color: '#BDBDBD',
  textTransform: 'none',
  '&:disabled': {
    backgroundColor: '#DDDDDD',
    color: '#BDBDBD',
  },
});

const WarningBox = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  padding: '12px 16px',
  backgroundColor: 'rgba(255, 193, 7, 0.1)',
  border: '1px solid #FFC107',
  borderRadius: '12px',
  gap: '12px',
  width: '100%',
});

const WarningIcon = styled(ErrorOutlineIcon)({
  color: '#FFC107',
  fontSize: '20px',
  flexShrink: 0,
  marginTop: '2px',
});

// Orange warning box for confirm-all step (matching Figma)
const ImportantWarningBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '16px',
  backgroundColor: '#FFF4E5',
  border: '1px solid #FFEDD5',
  borderRadius: '20px',
  gap: '8px',
  width: '100%',
});

const ImportantWarningHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
});

const ImportantWarningTitle = styled(Typography)({
  fontFamily: '"IBM Plex Sans Arabic", "Tajawal", sans-serif',
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: '24px',
  color: '#C45A11',
});

const ImportantWarningIcon = styled(ErrorOutlineIcon)({
  color: '#C45A11',
  fontSize: '20px',
});

const ImportantWarningText = styled(Typography)({
  fontFamily: '"IBM Plex Sans Arabic", "Tajawal", sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '24px',
  color: '#C45A11',
  textAlign: 'right',
  width: '100%',
});

const WarningText = styled(Typography)({
  fontFamily: '"IBM Plex Sans Arabic", "Tajawal", sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20px',
  color: '#666666',
});

const UnitPreviewCard = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px',
  backgroundColor: '#F9FAFB',
  border: '1px solid #E5E7EB',
  borderRadius: '20px',
  width: '100%',
});

const UnitInfo = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const UnitImage = styled('img')({
  width: '40px',
  height: '40px',
  borderRadius: '2.5x',
  objectFit: 'cover',
});

const UnitName = styled(Typography)({
  fontFamily: '"IBM Plex Sans Arabic", "Tajawal", sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '20px',
  color: '#262626',
});

const ChannelLogosContainer = styled(Box)({
  display: 'flex',
  gap: '4px',
});

const SmallChannelLogo = styled('img')({
  width: '24px',
  height: '24px',
  borderRadius: '4px',
  objectFit: 'contain',
});

const FooterContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  padding: '16px 32px 32px',
  gap: '12px',
  backgroundColor: '#ffffff',
});

const ButtonsContainer = styled(Box)({
  display: 'flex',
  gap: '12px',
  width: '100%',
});

const PrimaryButton = styled(Button)({
  flex: 1,
  height: '44px',
  borderRadius: '8px',
  backgroundColor: '#5B2FF9',
  fontFamily: '"IBM Plex Sans Arabic", "Tajawal", sans-serif',
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: '24px',
  color: '#ffffff',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#4a25d4',
  },
  '&:disabled': {
    backgroundColor: '#BFBFBF',
    color: '#ffffff',
  },
});

const SecondaryButton = styled(Button)({
  flex: 1,
  height: '44px',
  borderRadius: '8px',
  backgroundColor: 'transparent',
  border: '1px solid #E5E7EB',
  fontFamily: '"IBM Plex Sans Arabic", "Tajawal", sans-serif',
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: '24px',
  color: '#262626',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#F9FAFB',
  },
  '&:disabled': {
    color: '#BFBFBF',
  },
});

const DangerButton = styled(Button)({
  flex: 1,
  height: '44px',
  borderRadius: '8px',
  backgroundColor: '#E11900',
  fontFamily: '"IBM Plex Sans Arabic", "Tajawal", sans-serif',
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: '24px',
  color: '#ffffff',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#C41400',
  },
  '&:disabled': {
    backgroundColor: '#BFBFBF',
    color: '#ffffff',
  },
});

// Helper function to get channel logo type
const getChannelLogoType = (channelType: ChannelType): ChannelLogoType | null => {
  switch (channelType) {
    case ChannelType.Airbnb:
      return 'airbnb';
    case ChannelType.Booking:
      return 'booking';
    case ChannelType.Gathern:
      return 'gathern';
    default:
      return null;
  }
};

// Helper function to get channel name
const getChannelName = (channelType: ChannelType): string => {
  switch (channelType) {
    case ChannelType.Airbnb:
      return 'Airbnb';
    case ChannelType.Booking:
      return 'Booking';
    case ChannelType.Gathern:
      return 'Gathern';
    default:
      return '';
  }
};


type UnmergeOption = 'one' | 'all' | null;
type Step = 'select-option' | 'select-channels' | 'confirm-all';

interface UnmergeOptionsModalProps {
  open: boolean;
  onClose: () => void;
  unit: IMsoolUnit | null;
  onSuccess?: () => void;
}

const UnmergeOptionsModal: React.FC<UnmergeOptionsModalProps> = memo(({
  open,
  onClose,
  unit,
  onSuccess,
}) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

  // Get active channels from unit — exclude Airbnb since it's not sent in unmerge
  const activeChannels = useMemo(() => {
    if (!unit?.channels) return [];
    return unit.channels.filter(c => c.status === true && c.channelType !== ChannelType.Airbnb);
  }, [unit]);

  // Check if only one channel is connected - skip first step
  const hasSingleChannel = activeChannels.length === 1;

  // Determine initial step based on number of channels
  const initialStep: Step = hasSingleChannel ? 'confirm-all' : 'select-option';

  // State
  const [step, setStep] = useState<Step>(initialStep);
  const [selectedOption, setSelectedOption] = useState<UnmergeOption>(hasSingleChannel ? 'all' : null);
  const [selectedChannels, setSelectedChannels] = useState<ChannelType[]>([]);

  // Update step when modal opens with different channel count
  React.useEffect(() => {
    if (open) {
      if (hasSingleChannel) {
        setStep('confirm-all');
        setSelectedOption('all');
      } else {
        setStep('select-option');
        setSelectedOption(null);
      }
      setSelectedChannels([]);
    }
  }, [open, hasSingleChannel]);

  // Check if Airbnb is selected (for warning)
  const isAirbnbSelected = useMemo(() => {
    return selectedChannels.includes(ChannelType.Airbnb);
  }, [selectedChannels]);

  // Unmap mutation
  const { mutateAsync: unmapUnits, isPending: isUnmapping } = useUnmapUnits();

  // Reset state when modal opens/closes
  const handleClose = useCallback(() => {
    setStep(hasSingleChannel ? 'confirm-all' : 'select-option');
    setSelectedOption(hasSingleChannel ? 'all' : null);
    setSelectedChannels([]);
    onClose();
  }, [onClose, hasSingleChannel]);

  // Handle option selection
  const handleOptionSelect = useCallback((option: UnmergeOption) => {
    setSelectedOption(option);
  }, []);

  // Handle channel toggle
  const handleChannelToggle = useCallback((channelType: ChannelType) => {
    setSelectedChannels(prev => {
      if (prev.includes(channelType)) {
        return prev.filter(c => c !== channelType);
      }
      return [...prev, channelType];
    });
  }, []);

  // Handle next step
  const handleNext = useCallback(() => {
    if (step === 'select-option') {
      if (selectedOption === 'one') {
        setStep('select-channels');
      } else if (selectedOption === 'all') {
        setStep('confirm-all');
      }
    }
  }, [step, selectedOption]);

  // Handle back
  const handleBack = useCallback(() => {
    // If only one channel, close modal instead of going back
    if (hasSingleChannel) {
      handleClose();
      return;
    }
    if (step === 'select-channels' || step === 'confirm-all') {
      setStep('select-option');
      setSelectedChannels([]);
    }
  }, [step, hasSingleChannel, handleClose]);

  // Handle unmerge
  const handleUnmerge = useCallback(async () => {
    if (!unit) return;

    try {
      // Determine which channels to unmap
      // If Airbnb is selected, unmap ALL channels since Airbnb is the base channel
      const channelsToUnmap = step === 'confirm-all' || isAirbnbSelected
        ? activeChannels
        : activeChannels.filter(c => selectedChannels.includes(c.channelType));

      // Build units array for the API — exclude Airbnb from the payload
      const unitsToUnmap = channelsToUnmap
        .filter(channel => channel.userChannelId !== null && channel.channelType !== ChannelType.Airbnb)
        .map(channel => ({
          userChannelId: channel.userChannelId as number,
          channelType: channel.channelType,
          channelListingId: channel.channelListingId,
        }));

      if (unitsToUnmap.length === 0) return;

      await unmapUnits({
        propertyId: unit.propertyId,
        units: unitsToUnmap,
      });

      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error('Failed to unmap channels:', error);
    }
  }, [unit, step, activeChannels, selectedChannels, unmapUnits, onSuccess, handleClose]);

  // Render step 1: Select option
  const renderSelectOption = () => (
    <>
      <ContentContainer>
        <div>
          <Title>
            {t(T_KEYS.UNMERGE_TITLE) || 'فك الدمج'}
          </Title>
          <Description>
            {t(T_KEYS.UNMERGE_STEP1_DESCRIPTION) || 'اختر كيف تريد فصل هذا العقار عن القنوات المرتبطة به.'}
          </Description>
        </div>

        <OptionsContainer>
          <OptionCard
            $selected={selectedOption === 'one'}
            onClick={() => handleOptionSelect('one')}
          >
            <OptionContent>
              <OptionTitle>
                {t(T_KEYS.UNMERGE_ONE_OPTION_TITLE) || 'فك الدمج عن قناة واحدة'}
              </OptionTitle>
              <OptionDescription>
                {t(T_KEYS.UNMERGE_ONE_OPTION_DESC) || 'فصل هذا العقار عن قناة محددة فقط'}
              </OptionDescription>
            </OptionContent>
            <RadioCircle $selected={selectedOption === 'one'}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="24" height="24" rx="12" fill="#5B3FFF"/>
                  <circle cx="12" cy="11.9996" r="6.54545" fill="white"/>
                </svg>

            </RadioCircle>
          </OptionCard>

          <OptionCard
            $selected={selectedOption === 'all'}
            onClick={() => handleOptionSelect('all')}
          >
            <OptionContent>
              <OptionTitle>
                {t(T_KEYS.UNMERGE_ALL_OPTION_TITLE) || 'فك الدمج عن كل القنوات'}
              </OptionTitle>
              <OptionDescription>
                {t(T_KEYS.UNMERGE_ALL_OPTION_DESC) || 'فصل هذا العقار عن جميع القنوات المرتبطة'}
              </OptionDescription>
            </OptionContent>
            <RadioCircle $selected={selectedOption === 'all'}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="24" height="24" rx="12" fill="#5B3FFF"/>
                <circle cx="12" cy="11.9996" r="6.54545" fill="white"/>
              </svg>
            </RadioCircle>
          </OptionCard>
        </OptionsContainer>
      </ContentContainer>

      <FooterContainer>
        <ButtonsContainer>
          {!selectedOption ? (
            <DisabledButton disabled>
              {t(T_KEYS.CONTINUE) || 'متابعة'}
            </DisabledButton>
          ) : (
            <PrimaryButton onClick={handleNext}>
              {t(T_KEYS.CONTINUE) || 'متابعة'}
            </PrimaryButton>
          )}
          <SecondaryButton onClick={handleClose}>
            {t(T_KEYS.CANCEL) || 'إلغاء'}
          </SecondaryButton>
        </ButtonsContainer>
      </FooterContainer>
    </>
  );

  // Render step 2: Select channels (Flow 1)
  const renderSelectChannels = () => (
    <>
      <ContentContainer>
        <div>
          <Title>
            {t(T_KEYS.UNMERGE_ONE_CHANNEL_TITLE) || 'فك الدمج عن قناة واحدة'}
          </Title>
          <Description>
            {t(T_KEYS.UNMERGE_ONE_CHANNEL_DESCRIPTION) || 'اختر القناة التي تريد فك الدمج عنها لهذا العقار.'}
          </Description>
        </div>

        {/* Unit Preview */}
        {unit && (
          <UnitPreviewCard>
            <UnitInfo>
              {unit.imageUrl && (
                <UnitImage src={unit.imageUrl} alt={unit.propertyName || 'Unit'} />
              )}
              <UnitName>{unit.propertyName || t(T_KEYS.UNNAMED_UNIT)}</UnitName>
            </UnitInfo>
            <ChannelLogosContainer>
              {activeChannels.map((channel, index) => {
                const logoType = getChannelLogoType(channel.channelType);
                return logoType ? (
                  <UnifiedChannelLogo
                    key={index}
                    channel={logoType}
                    size="sm"
                    rounded
                  />
                ) : null;
              })}
            </ChannelLogosContainer>
          </UnitPreviewCard>
        )}

        {/* Section Title */}
        <SectionTitle>
          {t(T_KEYS.CONNECTED_CHANNELS) || 'القنوات المرتبطة'}
        </SectionTitle>

        {/* Horizontal Channel Cards */}
        <ChannelCardsContainer>
          {activeChannels.map((channel, index) => {
            const logoType = getChannelLogoType(channel.channelType);
            return (
              <ChannelCard
                key={index}
                $selected={selectedChannels.includes(channel.channelType)}
                onClick={() => handleChannelToggle(channel.channelType)}
              >
                <ChannelCardLogo>
                  {logoType && <UnifiedChannelLogo channel={logoType} size="md" />}
                </ChannelCardLogo>
                <ChannelCardName>{getChannelName(channel.channelType)}</ChannelCardName>
              </ChannelCard>
            );
          })}
        </ChannelCardsContainer>

        {/* Warning when Airbnb is selected */}
        {/* {isAirbnbSelected && (
          <ImportantWarningBox>
            <ImportantWarningHeader>
              <ImportantWarningIcon />
              <ImportantWarningTitle>
                {t(T_KEYS.IMPORTANT_NOTICE) || 'تنبيه مهم'}
              </ImportantWarningTitle>
            </ImportantWarningHeader>
            <ImportantWarningText>
              {t(T_KEYS.AIRBNB_BASE_CHANNEL_WARNING) || 'القناة Airbnb هي القناة الأساسية في مسؤول. عند فك الدمج عنها، سيفقد هذا العقار إمكانية الاستفادة من مميزات مسؤول.'}
            </ImportantWarningText>
          </ImportantWarningBox>
        )} */}
      </ContentContainer>

      <FooterContainer>
        <ButtonsContainer>
          {selectedChannels.length === 0 ? (
            <DisabledButton disabled>
              {t(T_KEYS.CONTINUE) || 'متابعة'}
            </DisabledButton>
          ) : (
            <PrimaryButton
              onClick={handleUnmerge}
              disabled={isUnmapping}
            >
              {isUnmapping ? (
                <CircularProgress size={20} sx={{ color: '#ffffff' }} />
              ) : (
                t(T_KEYS.CONTINUE) || 'متابعة'
              )}
            </PrimaryButton>
          )}
          <SecondaryButton onClick={handleBack} disabled={isUnmapping}>
            {t(T_KEYS.CANCEL) || 'إلغاء'}
          </SecondaryButton>
        </ButtonsContainer>
      </FooterContainer>
    </>
  );

  // Render step 2: Confirm all (Flow 2)
  const renderConfirmAll = () => (
    <>
      <ContentContainer>
        <div>
          <Title>
            {t(T_KEYS.UNMERGE_ALL_TITLE) || 'فك الدمج عن جميع القنوات'}
          </Title>
          <Description>
            {t(T_KEYS.UNMERGE_ALL_DESC) || 'سيؤدي هذا الإجراء إلى فك ربط هذا العقار عن القنوات التالية.'}
          </Description>
        </div>
        {/* Unit Preview with all channels */}
        {unit && (
          <UnitPreviewCard>
            <UnitInfo>
              {unit.imageUrl && (
                <UnitImage src={unit.imageUrl} alt={unit.propertyName || 'Unit'} />
              )}
              <UnitName>{unit.propertyName || t(T_KEYS.UNNAMED_UNIT)}</UnitName>
            </UnitInfo>
            <ChannelLogosContainer>
              {activeChannels.map((channel, index) => {
                const logoType = getChannelLogoType(channel.channelType);
                return logoType ? (
                  <UnifiedChannelLogo
                    key={index}
                    channel={logoType}
                    size="sm"
                    rounded
                  />
                ) : null;
              })}
            </ChannelLogosContainer>
          </UnitPreviewCard>
        )}

        {/* Important Warning Alert - Orange */}
        {/* <ImportantWarningBox>
          <ImportantWarningHeader>
            <ImportantWarningTitle>
              {t(T_KEYS.IMPORTANT_NOTICE) || 'تنبيه مهم'}
            </ImportantWarningTitle>
            <ImportantWarningIcon />
          </ImportantWarningHeader>
          <ImportantWarningText>
            {t(T_KEYS.AIRBNB_BASE_CHANNEL_WARNING) || 'القناة Airbnb هي القناة الأساسية في مسؤول. عند فك الدمج عنها، سيفقد هذا العقار إمكانية الاستفادة من مميزات مسؤول.'}
          </ImportantWarningText>
        </ImportantWarningBox> */}
      </ContentContainer>

      <FooterContainer>
        <ButtonsContainer>
          <PrimaryButton
            onClick={handleUnmerge}
            disabled={isUnmapping}
          >
            {isUnmapping ? (
              <CircularProgress size={20} sx={{ color: '#ffffff' }} />
            ) : (
              t(T_KEYS.UNMERGE) || 'فك الدمج'
            )}
          </PrimaryButton>
          <SecondaryButton onClick={handleBack} disabled={isUnmapping}>
            {t(T_KEYS.CANCEL) || 'إلغاء'}
          </SecondaryButton>
        </ButtonsContainer>
      </FooterContainer>
    </>
  );

  return (
    <StyledDialog open={open} onClose={handleClose}>
      {step === 'select-option' && renderSelectOption()}
      {step === 'select-channels' && renderSelectChannels()}
      {step === 'confirm-all' && renderConfirmAll()}
    </StyledDialog>
  );
});

UnmergeOptionsModal.displayName = 'UnmergeOptionsModal';

export default UnmergeOptionsModal;
